import { Chart, ChartEditor, Table, useDashboard, useDashboardReport, useExport, useQuill } from "@quillsql/react"
import { useState } from "react"

import { AccessCheck } from "@particles"

import { Button } from "@atoms"

import { ResponsiveDialog } from "@molecules"

import { EDITABLE_SECTION_NAME } from "@pages/Reports/ReportDashboard/ReportDashboard.constants"
import ReportEditorDialog from "@pages/Reports/ReportDashboard/ReportEditorDialog/ReportEditorDialog"
import { QuillButton, QuillDeleteButton } from "@pages/Reports/ReportDashboard/components/QuillButton/QuillButton"
import QuillDialog from "@pages/Reports/ReportDashboard/components/QuillDialog/QuillDialog"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./ReportDetailDialog.module.scss"
import { ReportDetailDialogProps } from "./ReportDetailDialog.types"

export default function ReportDetailDialog(props: ReportDetailDialogProps) {
    const { isOpen, onOpenChange, reportId, dashboardId } = props

    const { data, loading, error } = useQuill(reportId)
    const { sections, isLoading: sectionsLoading } = useDashboard(dashboardId)
    const { deleteReport } = useDashboardReport(reportId)
    const { downloadCSV, isCSVLoading } = useExport(reportId)
    const [isEditChartDialogOpen, setIsEditChartDialogOpen] = useState(false)
    const [isEditQueryDialogOpen, setIsEditQueryDialogOpen] = useState(false)

    const reportIsEditable = sections?.[EDITABLE_SECTION_NAME]?.some((report) => report.id === reportId) ?? false

    const handleEditChart = () => {
        reportIsEditable && setIsEditChartDialogOpen(true)
    }

    const handleEditQuery = () => {
        reportIsEditable && setIsEditQueryDialogOpen(true)
    }

    const handleDeleteReport = () => {
        if (reportIsEditable) {
            deleteReport()
            onOpenChange(false)
        }
    }

    const handleDownloadCSV = () => {
        void downloadCSV()
    }

    if (loading || sectionsLoading) {
        return (
            <ResponsiveDialog.Root headerTitle="Loading..." isOpen={isOpen} onOpenChange={onOpenChange}>
                <ResponsiveDialog.Body>
                    <div className={styles.loading}>Loading report...</div>
                </ResponsiveDialog.Body>
            </ResponsiveDialog.Root>
        )
    }

    if (error) {
        return (
            <ResponsiveDialog.Root headerTitle="Error" isOpen={isOpen} onOpenChange={onOpenChange}>
                <ResponsiveDialog.Body>
                    <div className={styles.error}>Error loading report</div>
                </ResponsiveDialog.Body>
            </ResponsiveDialog.Root>
        )
    }

    return (
        <>
            <QuillDialog title={data?.name || "Report"} isOpen={isOpen} setIsOpen={onOpenChange}>
                <div className={styles.container}>
                    <div className={styles.actions}>
                        {reportIsEditable && (
                            <AccessCheck
                                permissions={{
                                    reports_edit_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <Button colorScheme="gray" variant="outline" size="sm" onClick={handleEditChart}>
                                    Edit Chart
                                </Button>
                                <Button colorScheme="gray" variant="outline" size="sm" onClick={handleEditQuery}>
                                    Edit Query
                                </Button>
                            </AccessCheck>
                        )}
                        <Button
                            colorScheme="gray"
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadCSV}
                            isLoading={isCSVLoading}
                        >
                            Download CSV
                        </Button>
                        {reportIsEditable && (
                            <AccessCheck
                                permissions={{
                                    reports_edit_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <Button colorScheme="tomato" variant="outline" size="sm" onClick={handleDeleteReport}>
                                    Delete Report
                                </Button>
                            </AccessCheck>
                        )}
                    </div>
                    <div className={styles.chart}>
                        <Chart reportId={reportId} />
                    </div>
                    {data?.chartType !== "table" && data?.chartType !== "US map" && (
                        <div className={styles.table}>
                            <Table reportId={reportId} />
                        </div>
                    )}
                </div>
            </QuillDialog>
            <ReportEditorDialog
                isOpen={isEditQueryDialogOpen}
                onOpenChange={setIsEditQueryDialogOpen}
                dashboardId={dashboardId}
                reportId={reportId}
            />
            <ChartEditor
                isOpen={isEditChartDialogOpen}
                setIsOpen={setIsEditChartDialogOpen}
                reportId={reportId}
                destinationDashboard={dashboardId}
                destinationSection={EDITABLE_SECTION_NAME}
                showTableFormatOptions={true}
                showDashboardFilterFields={true}
                hideDeleteButton={true}
                ButtonComponent={QuillButton}
                SecondaryButtonComponent={QuillButton}
                DeleteButtonComponent={QuillDeleteButton}
                ModalComponent={QuillDialog}
            />
        </>
    )
}
